import _ from 'underscore';

import {initializeAmplitude} from 'analytics/amplitude';
import * as GoogleTagManagerForAI from 'analytics/GoogleTagManagerForAI';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {initializeZendesk} from 'nav/ZendeskMessenger';

export enum ConsentState {
  // The user clicked "Yes" for the destination's category.
  EXPLICIT_AGREE = 'EXPLICIT_AGREE',
  // The user clicked "No" for the destination's category.
  EXPLICIT_DISAGREE = 'EXPLICIT_DISAGREE',
  // The user clicked "Cancel" and agreed to proceed anyway. We track this as a separate state so
  // that if the user revisits the modal, the radio button will show up as unset.
  IMPLICIT_AGREE = 'IMPLICIT_AGREE',
  // State used internally in the modal to indicate that no choice has been made. This should never
  // be saved to the prefs cookie.
  UNSPECIFIED = 'UNSPECIFIED',
}

/**
 * An object mapping specific destination to whether or not the user has approved it. This object
 * is saved in a session cookie.
 *
 * Values may also be missing, indicating that the user has neither implicitly nor explicitly
 * agreed. In this case, we need to open the modal to get some kind of agreement from the user.
 */
export interface ConsentPreferences {
  [destination: string]: ConsentState;
}

export enum DestinationCategory {
  FUNCTIONAL = 'functional',
  MARKETING_AND_ANALYTICS = 'marketingAndAnalytics',
  ADVERTISING = 'advertising',
  ESSENTIAL = 'essential', // We do not allow users to opt out of ESSENTIAL destinations.
}

export interface Destination {
  id: DestinationId;
  name: string;
  category: DestinationCategory;
}

export enum DestinationId {
  GOOGLE_TAG_MANAGER = 'Google Tag Manager',
  FACEBOOK_PIXEL = 'Facebook Pixel',
  LINKEDIN_INSIGHT_TAG = 'LinkedIn Insight Tag',
  SPLIT = 'Split',
  GOOGLE_ANALYTICS = 'Google Analytics',
  AMPLITUDE = 'Amplitude',
  WEBHOOKS = 'Webhooks',
  ADROLL = 'AdRoll',
  CLEARBIT_REVEAL = 'Clearbit Reveal',
  ZENDESK = 'Zendesk',
  GAINSIGHT_PX = 'GainsightPx',
  GOOGLE_TAG_MANAGER_AI = 'Google Tag Manager (AI)',
}

export const DESTINATIONS: Array<Destination> = [
  {
    id: DestinationId.GOOGLE_TAG_MANAGER,
    name: 'Google Tag Manager (Bing Event Tracking, Loop Analytics, Hotjar)',
    category: DestinationCategory.ADVERTISING,
  },
  {
    id: DestinationId.GOOGLE_TAG_MANAGER_AI,
    name: 'Google Tag Manager (Marketo)',
    category: DestinationCategory.ADVERTISING,
  },
  {id: DestinationId.FACEBOOK_PIXEL, name: 'Facebook Pixel', category: DestinationCategory.ADVERTISING},
  {
    id: DestinationId.LINKEDIN_INSIGHT_TAG,
    name: 'LinkedIn Insight Tag',
    category: DestinationCategory.ADVERTISING,
  },
  {
    id: DestinationId.SPLIT,
    name: 'Split',
    category: DestinationCategory.MARKETING_AND_ANALYTICS,
  },
  {id: DestinationId.AMPLITUDE, name: 'Amplitude', category: DestinationCategory.MARKETING_AND_ANALYTICS},
  {
    id: DestinationId.ADROLL,
    name: 'AdRoll',
    category: DestinationCategory.ADVERTISING,
  },
  {
    id: DestinationId.GOOGLE_ANALYTICS,
    name: 'Google Analytics',
    category: DestinationCategory.MARKETING_AND_ANALYTICS,
  },
  {id: DestinationId.ZENDESK, name: 'Zendesk', category: DestinationCategory.ESSENTIAL},
  {id: DestinationId.GAINSIGHT_PX, name: 'GainsightPx', category: DestinationCategory.ESSENTIAL},
  {
    id: DestinationId.CLEARBIT_REVEAL,
    name: 'Clearbit Reveal',
    category: DestinationCategory.MARKETING_AND_ANALYTICS,
  },
];

export const ESSENTIAL_DESTINATION_IDS: Array<string> = DESTINATIONS.filter(
  (destination) => destination.category === DestinationCategory.ESSENTIAL
).map((destination) => destination.id);

export function destinationById(destinationId: string): Destination {
  const destination = _.findWhere(DESTINATIONS, {id: destinationId});
  if (!destination) {
    // Fall back to marketing and analytics if it's unrecognized, but we should be documenting each
    // possible destination here.
    assert(false, `Expected destination info for ID ${destinationId}.`);
    return {
      id: destinationId as DestinationId,
      name: destinationId,
      category: DestinationCategory.MARKETING_AND_ANALYTICS,
    };
  }
  return destination;
}

export function getNonSegmentDestinationIds(): Array<string> {
  const destinationIds = [];
  if (BenchlingConfig.ENABLE_ZENDESK_MESSAGING) {
    destinationIds.push('Zendesk');
  }
  if (BenchlingConfig.ENABLE_GAINSIGHT_PX) {
    destinationIds.push('GainsightPx');
  }
  if (GoogleTagManagerForAI.isRequired()) {
    destinationIds.push(DestinationId.GOOGLE_TAG_MANAGER_AI);
  }
  if (BenchlingConfig.ANALYTICS_ENABLE_AMPLITUDE) {
    destinationIds.push(DestinationId.AMPLITUDE);
  }
  return destinationIds;
}

/**
 * This should be called once per page visit, and does analytics initialization when the user's
 * preferences have been decided (either on initial pageload or when the user completes the consent
 * modal via save or cancel). This includes both Segment (window.analytics) and anything else
 * needing a cookie.
 *
 * Note that this is the raw ConsentPreferences object, where a missing value indicates that the
 * user has not given implicit or explicit consent. If this method is called with the prefs in such
 * a state, it means that we don't need consent, so we should enable.
 *
 * `isConsentRequired` is true in the EU (or when force-requiring the consent manager). Amplitude
 * identity cookies are only written after EXPLICIT_AGREE in that case; elsewhere cookies are OK
 * without a modal.
 */
export function initAnalyticsFromPreferences(
  preferences: ConsentPreferences,
  isConsentRequired: boolean
): void {
  const {bootstrapUser} = window;

  if (window.analytics) {
    // For segment integrations the CookieConsentManagerContainer component already handles most of
    // the work, but does not call page() or identify(), so we need to call it ourselves. The
    // analytics global is loaded async around the time that this function is called, and only
    // actually loaded if the user agrees to any integrations, so we need to use `ready` to wait for
    // it to load, and calling `ready` will properly disable
    window.analytics.ready(() => {
      // Identify the user first so `page` will be identified if the user is logged in.
      if (bootstrapUser) {
        window.analytics.identify(bootstrapUser.entityId, bootstrapUser.publicIdentifyTraits);
      }
      window.analytics.page();
    });
  }

  // We do not check if users have consented to Zendesk because it is an ESSENTIAL destination.
  if (BenchlingConfig.ENABLE_ZENDESK_MESSAGING && BenchlingConfig.ENABLE_IN_APP_SUPPORT) {
    initializeZendesk();
  }

  // We do not check if users have consented to Gainsight because it is an ESSENTIAL destination.
  if (BenchlingConfig.ENABLE_GAINSIGHT_PX && window.initializeGainsightPx) {
    window.initializeGainsightPx();
  }

  if (
    GoogleTagManagerForAI.isRequired() &&
    preferences[DestinationId.GOOGLE_TAG_MANAGER_AI] !== ConsentState.EXPLICIT_DISAGREE
  ) {
    GoogleTagManagerForAI.initialize();
  }

  if (BenchlingConfig.ANALYTICS_ENABLE_AMPLITUDE) {
    initializeAmplitude(window.AMPLITUDE_API_KEY, preferences, isConsentRequired);
  }
}
